import React, { useState, useEffect } from 'react';
import './AdminSurvey.css';

const BACKEND_URL = process.env.NODE_ENV === 'production'
  ? 'https://jog-backend.onrender.com'
  : 'http://localhost:5000';

const AdminSurvey = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');

  const [selectedSurvey, setSelectedSurvey] = useState('founder');
  const [selectedMode, setSelectedMode] = useState('dropdown');

  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState('');
  const [totalRows, setTotalRows] = useState(0);

  const [startRow, setStartRow] = useState('');
  const [endRow, setEndRow] = useState('');

  const [surveyData, setSurveyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generatingPptx, setGeneratingPptx] = useState(false);

  const verifyPassword = async (pwd) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pwd })
      });

      const data = await response.json();

      if (data.success) {
        setIsAuthenticated(true);
        setAuthError('');
        setPassword(pwd);
      } else {
        setAuthError('Invalid password');
      }
    } catch (err) {
      setAuthError('Connection error. Please try again.');
      console.error('Auth error:', err);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    verifyPassword(password);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    setSurveyData(null);
    setEmails([]);
  };

  // Fetch emails when survey type changes (for dropdown mode)
  useEffect(() => {
    if (isAuthenticated && selectedMode === 'dropdown') {
      fetchEmails();
    }
  }, [isAuthenticated, selectedSurvey, selectedMode]);

  const fetchEmails = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${BACKEND_URL}/api/admin/get_survey_data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: password,
          survey: selectedSurvey,
          mode: 'list'
        })
      });

      const data = await response.json();

      if (response.ok) {
        setEmails(data.emails || []);
        setTotalRows(data.totalRows || 0);
      } else {
        setError(data.message || 'Failed to fetch emails');
      }
    } catch (err) {
      setError('Failed to fetch emails');
      console.error('Fetch emails error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchData = async () => {
    if (selectedMode === 'dropdown' && !selectedEmail) {
      setError('Please select an email');
      return;
    }

    if (selectedMode === 'range') {
      if (!startRow || !endRow) {
        setError('Please enter both start and end row numbers');
        return;
      }
      if (parseInt(startRow) < 2) {
        setError('Start row must be at least 2 (row 1 is headers)');
        return;
      }
      if (parseInt(startRow) > parseInt(endRow)) {
        setError('Start row must be less than or equal to end row');
        return;
      }
    }

    try {
      setLoading(true);
      setError('');
      setSurveyData(null);

      const payload = {
        password: password,
        survey: selectedSurvey,
        mode: selectedMode
      };

      if (selectedMode === 'dropdown') {
        payload.email = selectedEmail;
      } else if (selectedMode === 'range') {
        payload.startRow = parseInt(startRow);
        payload.endRow = parseInt(endRow);
      }

      const response = await fetch(`${BACKEND_URL}/api/admin/get_survey_data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        console.log('Fetched survey data:', { mode: selectedMode, rowCount: data.data?.length, headers: data.headers?.length });
        setSurveyData(data);
      } else {
        setError(data.message || 'Failed to fetch data');
      }
    } catch (err) {
      setError('Failed to fetch survey data');
      console.error('Fetch data error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePptx = async () => {
    if (!surveyData || !surveyData.data || surveyData.data.length === 0) {
      setError('No survey data to generate report');
      return;
    }

    try {
      setGeneratingPptx(true);
      setError('');

      // Helper function to extract user data from a row
      const extractUserData = (row) => {
        let name, company, jobTitle, email, surveyResponses;

        if (selectedSurvey === 'founder') {
          name = row[0];
          company = row[1];
          email = row[3];
          jobTitle = row[4];
          surveyResponses = row.slice(5);
        } else {
          name = row[0];
          company = row[1];
          jobTitle = row[2];
          email = row[3];
          surveyResponses = row.slice(4);
        }

        return { name, company, jobTitle, email, surveyResponses };
      };

      // Check if we have multiple users
      // Use batch mode when there's more than 1 row of data
      const rowCount = surveyData.data.length;
      console.log('Generate PPTX - Mode:', selectedMode, 'Data rows:', rowCount);
      console.log('Survey data:', JSON.stringify(surveyData.data.slice(0, 2))); // Log first 2 rows for debugging

      if (rowCount > 1) {
        // Batch generation - multiple users
        console.log(`Generating PPTX for ${surveyData.data.length} users...`);

        const users = surveyData.data.map(row => extractUserData(row)).filter(u => u.name && u.company && u.jobTitle);

        if (users.length === 0) {
          setError('No valid user data found in selected rows');
          return;
        }

        const response = await fetch(`${BACKEND_URL}/api/admin/generate_pptx_batch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            password: password,
            users: users
          })
        });

        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `Joy_of_Growth_Reports_${new Date().toISOString().split('T')[0]}.zip`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          console.log(`${users.length} PPTX files downloaded as ZIP`);
        } else {
          const errorData = await response.json();
          setError(errorData.message || 'Failed to generate PowerPoint batch');
        }
      } else {
        // Single user generation
        console.log('Using single-user generation mode');
        const { name, company, jobTitle, email, surveyResponses } = extractUserData(surveyData.data[0]);

        if (!name || !company || !jobTitle) {
          setError('Missing required data in survey row');
          return;
        }

        console.log('Generating PPTX for:', { name, company, jobTitle, email, responseCount: surveyResponses.length });

        const response = await fetch(`${BACKEND_URL}/api/admin/generate_pptx`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            password: password,
            name: name,
            company: company,
            jobTitle: jobTitle,
            email: email,
            surveyResponses: surveyResponses
          })
        });

        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `Joy_of_Growth_Report_${name.replace(/\s+/g, '_')}.pptx`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          console.log('PPTX downloaded successfully');
        } else {
          const errorData = await response.json();
          setError(errorData.message || 'Failed to generate PowerPoint');
        }
      }
    } catch (err) {
      setError('Failed to generate PowerPoint');
      console.error('Generate PPTX error:', err);
    } finally {
      setGeneratingPptx(false);
    }
  };

  // Render login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="admin-wrapper">
        <div className="admin-container">
          <h1>Survey Admin Panel</h1>
          <form onSubmit={handleLogin} className="admin-login-form">
            <div className="admin-input-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                required
              />
            </div>
            {authError && <p className="admin-error">{authError}</p>}
            <button type="submit">Login</button>
          </form>
        </div>
      </div>
    );
  }

  // Render main admin interface
  return (
    <div className="admin-wrapper">
      <div className="admin-container">
        <div className="admin-header">
          <h1>Survey Admin Panel</h1>
          <button onClick={handleLogout} className="admin-logout-btn">Logout</button>
        </div>

        <div className="admin-controls">
          <div className="admin-input-group">
            <label htmlFor="survey">Survey Type</label>
            <select
              id="survey"
              value={selectedSurvey}
              onChange={(e) => {
                setSelectedSurvey(e.target.value);
                setSurveyData(null);
                setSelectedEmail('');
              }}
            >
              <option value="founder">Founder</option>
              <option value="marketing">Marketing Leaders</option>
            </select>
          </div>

          <div className="admin-input-group">
            <label htmlFor="mode">Selection Mode</label>
            <select
              id="mode"
              value={selectedMode}
              onChange={(e) => {
                setSelectedMode(e.target.value);
                setSurveyData(null);
                setSelectedEmail('');
                setStartRow('');
                setEndRow('');
              }}
            >
              <option value="dropdown">Pick from dropdown</option>
              <option value="range">Pick range</option>
            </select>
          </div>

          {selectedMode === 'dropdown' && (
            <div className="admin-input-group">
              <label htmlFor="email">Select Email</label>
              <select
                id="email"
                value={selectedEmail}
                onChange={(e) => setSelectedEmail(e.target.value)}
              >
                <option value="">-- Select an email --</option>
                {emails.map((item, index) => (
                  <option key={index} value={item.email}>
                    {item.email} (Row {item.rowNumber})
                  </option>
                ))}
              </select>
            </div>
          )}

          {selectedMode === 'range' && (
            <>
              <div className="admin-input-group">
                <label htmlFor="startRow">Start Row (min: 2)</label>
                <input
                  type="number"
                  id="startRow"
                  value={startRow}
                  onChange={(e) => setStartRow(e.target.value)}
                  placeholder="e.g., 2"
                  min="2"
                />
              </div>
              <div className="admin-input-group">
                <label htmlFor="endRow">End Row (max: {totalRows || '?'})</label>
                <input
                  type="number"
                  id="endRow"
                  value={endRow}
                  onChange={(e) => setEndRow(e.target.value)}
                  placeholder="e.g., 10"
                  min="2"
                  max={totalRows}
                />
              </div>
              {totalRows > 0 && (
                <p className="admin-info">Total rows in sheet: {totalRows}</p>
              )}
            </>
          )}

          <button
            onClick={handleFetchData}
            disabled={loading}
            className="admin-fetch-btn"
          >
            {loading ? 'Loading...' : 'Fetch Data'}
          </button>
        </div>

        {error && <p className="admin-error">{error}</p>}

        {surveyData && (
          <div className="admin-results">
            <div className="admin-results-header">
              <div>
                <h2>Survey Data</h2>
                <p className="admin-info">
                  Showing {surveyData.data?.length || 0} row(s)
                </p>
              </div>
              <button
                onClick={handleGeneratePptx}
                disabled={generatingPptx || !surveyData.data || surveyData.data.length === 0}
                className="admin-generate-pptx-btn"
              >
                {generatingPptx ? 'Generating...' : 'Generate PPTX'}
              </button>
            </div>
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    {surveyData.headers?.map((header, index) => (
                      <th key={index}>{header || `Column ${index + 1}`}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {surveyData.data?.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <td key={cellIndex}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSurvey;
